"use client";

import { motion, useInView } from "framer-motion";
import { useRef } from "react";
import { FileDiff, Brain, Target, Trophy } from "lucide-react";

const thinkingSteps = [
    {
        icon: FileDiff,
        title: "diff analysis",
        description:
            "analyzes what actually changed, not the entire codebase. focuses on new logic, modified functions, and potential breaking changes. ignores cosmetic updates.",
        details: "semantic diff parsing → change classification → impact assessment"
    },
    {
        icon: Brain,
        title: "repo memory",
        description:
            "remembers your codebase patterns, common mistakes, and architectural decisions. learns from past reviews to provide more relevant feedback over time.",
        details: "pattern recognition → historical context → adaptive learning"
    },
    {
        icon: Target,
        title: "risk scoring",
        description:
            "prioritizes issues by merge-blocking potential. security vulnerabilities get highest priority, followed by bugs, then performance concerns.",
        details: "severity assessment → impact evaluation → priority ranking"
    },
    {
        icon: Trophy,
        title: "comment ranking",
        description:
            "shows most critical issues first. groups related problems together. limits noise to keep reviews actionable and focused.",
        details: "importance weighting → issue clustering → noise filtering"
    },
];

export function HowItThinks() {
    const ref = useRef(null);
    const isInView = useInView(ref, { once: true, margin: "-100px" });

    return (
        <section ref={ref} className="py-24 px-4 bg-background border-t border-border">
            <div className="container mx-auto max-w-6xl">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
                    transition={{ duration: 0.6 }}
                    className="text-center mb-16"
                >
                    <h2 className="text-4xl md:text-5xl font-bold mb-4 tracking-tight text-zinc-900 dark:text-white">
                        how it thinks
                    </h2>
                    <p className="text-zinc-600 dark:text-zinc-400 text-lg max-w-2xl mx-auto">
                        burg ai doesn&apos;t just scan code. it understands context, prioritizes risks, and learns from your patterns.
                    </p>
                </motion.div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-8 relative">
                    {/* Central connecting line for mobile could be added here if desired, but sticking to desktop fix first */}

                    {thinkingSteps.map((step, index) => {
                        const Icon = step.icon;
                        const isLastRow = index >= 2;

                        return (
                            <motion.div
                                key={step.title}
                                initial={{ opacity: 0, y: 20 }}
                                animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
                                transition={{ duration: 0.4, delay: index * 0.1 }}
                                className="relative group"
                            >
                                <div className="p-6 h-full rounded-2xl border border-zinc-200 dark:border-border bg-white/50 dark:bg-card/50 backdrop-blur-sm hover:border-primary/50 hover:bg-white dark:hover:bg-card hover:shadow-lg hover:shadow-primary/5 transition-all duration-300">
                                    <div className="flex items-start gap-5">
                                        <div className="flex-shrink-0">
                                            <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center group-hover:scale-110 group-hover:bg-primary/20 transition-all duration-300">
                                                <Icon className="w-6 h-6 text-primary" />
                                            </div>
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <h3 className="text-xl font-bold mb-2 tracking-tight text-zinc-900 dark:text-white">{step.title}</h3>
                                            <p className="text-zinc-600 dark:text-zinc-400 text-sm leading-relaxed mb-4">
                                                {step.description}
                                            </p>
                                            <div className="text-xs font-mono text-zinc-700 dark:text-primary/80 bg-zinc-100 dark:bg-primary/5 border border-zinc-200 dark:border-primary/10 px-3 py-1.5 rounded-md inline-block">
                                                {step.details}
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Connection line for desktop - only for top row */}
                                {!isLastRow && (
                                    <div className="hidden md:block absolute -bottom-8 left-12 w-0.5 h-8 bg-gradient-to-b from-zinc-200 dark:from-border to-transparent opacity-50"></div>
                                )}
                            </motion.div>
                        );
                    })}
                </div>

                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
                    transition={{ duration: 0.6, delay: 0.6 }}
                    className="mt-16 text-center"
                >
                    <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white dark:bg-card border border-zinc-200 dark:border-border text-sm text-zinc-600 dark:text-muted-foreground shadow-sm">
                        <span>🎯</span>
                        <span>precision over perfection</span>
                    </div>
                </motion.div>
            </div>
        </section>
    );
}